"use client";

import { APP, MODALS } from "@idss/libraries";
import { handleError, handleSuccess } from "@idss/notification";
import {
  Button,
  Divider,
  Flex,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { Form } from "@mantine/form";
import { modals } from "@mantine/modals";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { builder } from "@/builders";
import { ProfileData } from "@/builders/types/profile";
import { StaffListDataUpdate } from "@/builders/types/staff-list";

import { cast } from "@/libraries/cast";
import { useCookies } from "next-client-cookies";
import { ChangePassword } from "../change-password";
import { useFormContext } from "./edit-profile-form-context";
import { EnableTwoFactorAuthentication } from "./enable-two-factor-authentication";
import { ProfileImage } from "./profile-image";

interface SettingsPanelProps extends ProfileData {}

export function SettingsPanel(props: SettingsPanelProps) {
  const { email, image } = props;

  const cookies = useCookies();
  const organization_id = cast.number(cookies.get(APP.ORGANIZATION_ID));

  const form = useFormContext();
  const disableEditProfile = !form.values.edit_profile;

  const { data: careers = [], isLoading: careersLoading } = useQuery({
    queryKey: builder.career_path.get.$get(),
    queryFn: () => builder.$use.career_path.get(),
    select: ({ data }) => {
      return data.map(({ name, id }) => ({
        value: `${id}`,
        label: name,
      }));
    },
  });

  const { data: departments = [], isLoading: departmentsLoading } = useQuery({
    queryKey: builder.departments.get.$get(),
    queryFn: () => builder.$use.departments.get(),
    select: ({ data }) => {
      return data.map(({ name, id }) => ({
        value: `${id}`,
        label: name,
      }));
    },
  });

  const { data: ranks = [], isLoading: ranksLoading } = useQuery({
    queryKey: builder.ranks.list.$get(),
    queryFn: () => builder.$use.ranks.list(),
    select: ({ data }) => {
      return (
        organization_id
          ? data.filter(
              ({ organization }) => organization.id === organization_id
            )
          : data
      ).map(({ name, id }) => ({
        value: `${id}`,
        label: name,
      }));
    },
  });

  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: builder.$use.staffs.update,
    onSuccess: () => {
      handleSuccess({
        title: "Operation Successful",
        message: "Profile updated successfully",
      });

      queryClient.invalidateQueries({
        queryKey: builder.account.profile.get.$get(),
      });

      form.setFieldValue("edit_profile", false);
    },
    onError: handleError({
      title: "Operation Failed",
    }),
  });

  function handleSubmit(values: StaffListDataUpdate) {
    mutate(values);
  }

  function handleChangePassword() {
    modals.open({
      title: "Change Password",
      children: <ChangePassword />,
      withCloseButton: false,
      modalId: MODALS.CHANGE_PASSWORD,
    });
  }

  return (
    <Stack p={32} gap={32}>
      <Form form={form} onSubmit={handleSubmit}>
        <Stack
          gap={0}
          className="border rounded-2xl border-primary-border-light bg-primary-background-white"
        >
          <Stack p={24} className="border-b border-b-primary-border-light">
            <h2 className="text-primary-text-body prose-lg/medium">
              Profile Details
            </h2>
          </Stack>

          <Flex p={24} gap={26} align="start">
            <ProfileImage url={image} />

            <Stack gap={24} flex={1}>
              <SimpleGrid
                cols={{
                  base: 1,
                  sm: 2,
                }}
                spacing={20}
              >
                <TextInput
                  label="First name"
                  withAsterisk
                  disabled={disableEditProfile}
                  {...form.getInputProps("first_name")}
                />

                <TextInput
                  label="Last name"
                  withAsterisk
                  disabled={disableEditProfile}
                  {...form.getInputProps("last_name")}
                />

                <TextInput
                  label="Phone number"
                  withAsterisk
                  disabled={disableEditProfile}
                  {...form.getInputProps("phone_number")}
                />

                <Select
                  data={ranks}
                  label="Rank"
                  withAsterisk
                  disabled={ranksLoading || disableEditProfile}
                  {...form.getInputProps("rank_id")}
                />

                <Select
                  data={departments}
                  label="Modules"
                  withAsterisk
                  disabled={departmentsLoading || disableEditProfile}
                  {...form.getInputProps("department_id")}
                />

                <Select
                  data={careers}
                  label="Career path"
                  withAsterisk
                  disabled={careersLoading || disableEditProfile}
                  {...form.getInputProps("career_path_id")}
                />
              </SimpleGrid>

              {!disableEditProfile ? (
                <Button w="fit-content" className="self-end" type="submit">
                  Save Profile
                </Button>
              ) : null}
            </Stack>
          </Flex>
        </Stack>
      </Form>

      <Stack
        className="border rounded-2xl border-primary-border-light bg-primary-background-white"
        pb={24}
      >
        <Stack p={24} className="border-b border-b-primary-border-light">
          <h2 className="text-primary-text-body prose-lg/medium">
            Sign-in Method
          </h2>
        </Stack>
        <Stack px={24}>
          <article>
            <h2 className="text-primary-text-body prose-lg/medium">
              Email Address
            </h2>
            <Text className="text-primary-text-subtle prose-lg/regular">
              {email}
            </Text>
          </article>

          <Divider />

          <Flex justify="space-between">
            <article>
              <h2 className="text-primary-text-body prose-lg/medium">
                Password
              </h2>
              <Text className="text-primary-text-subtle prose-lg/regular">
                ******************
              </Text>
            </article>

            <Button
              c="gray.11"
              color="gray.4"
              variant="filled"
              onClick={handleChangePassword}
            >
              Change Password
            </Button>
          </Flex>

          <EnableTwoFactorAuthentication />
        </Stack>
      </Stack>
    </Stack>
  );
}
